import { log } from "./log"

const getArticle = (type) => {
  return type?.match('^[aieouAIEOU].*') ? 'an' : 'a'
}
const decimalValidator = (value) => {
  const decimalPattern = /^-?\d*\.\d{1,8}$/
  return decimalPattern.test(value)
}

const isFieldUnique = (apiService, path, apiParam, uuid, type, fieldLabel) => {
  return async (rule, value, callback) => {
    if (value) {
      try {
        const externalId = uuid || ''
        const isValid = await apiService.canSave(path, { [apiParam]: value, externalId })
        if (isValid) {
          callback()
        } else {
          const article = getArticle(type)
          callback(
            new Error(
              `${fieldLabel} must be unique. There already exists ${article} ${type} with ${fieldLabel.toLowerCase()} ${value}.`
            )
          )
        }
      } catch (error) {
        callback(new Error('Validation failed. Please try again.'))
      }
    }
  }
}

const validationRules = {
  durationValidator: (rule, value) => {
    if (value <= 0) {
      return new Error('Invalid duration')
    }
    return true
  },

  isValidTag: (rule, value) => {
    const regExp = /^[-+]?[0-9A-Fa-f]+\.?[0-9A-Fa-f]*?$/
    if (value <= 0) {
      for (const tag of value) {
        if (tag.length < 24 || tag.length > 32) {
          return new Error('Tag length must be between configured min and max lengths.')
        } else if (!regExp.test(tag)) {
          return new Error('Tag must be in hexadecimal format')
        }
      }
    }

    return true
  },

  isIdbEntityNameUnique: (apiService, path, uuid, type) => {
    return isFieldUnique(apiService, path, 'uri', uuid, type, 'Name')
  },
  isNameUnique: (apiService, path, uuid, type) => {
    return isFieldUnique(apiService, path, 'name', uuid, type, 'Name')
  },
  isRoleIdentifierUnique: (apiService, path, uuid, type) => {
    return isFieldUnique(apiService, path, 'identifier', uuid, type, 'Role Identifier')
  },
  isUsernameUnique: (apiService, path, uuid) => {
    return isFieldUnique(apiService, path, 'handle', uuid, 'User', 'Username')
  },
  isUserEmailUnique: (apiService, path, uuid) => {
    return isFieldUnique(apiService, path, 'handle', uuid, 'User', 'Email')
  },
  isVehicleSerialNumberUnique: (apiService, path, uuid) => {
    return isFieldUnique(apiService, path, 'serialNumber', uuid, 'Vehicle', 'Serial Number')
  },
  isRfidUnique: (apiService, path, uuid) => async (rule, value, callback) => {
    const errors = []
    await Promise.all(
      value.map(async (tag) => {
        if (tag) {
          const paddedTag = tag.padStart(24, "0")
          try {
            const externalId = uuid || ''
            const isValid = await apiService.canSave(path, { rfid: paddedTag, externalId })
            if (!isValid) {
              errors.push(`Tags must be unique. Tag ${paddedTag} already exists in system.`)
            }
          } catch (error) {
            errors.push(`Validation failed for tag ${paddedTag}. Please try again.`)
          }
        }
      })
    )
    if (errors.length > 0) {
      callback(new Error(errors.join(' ')))
    } else {
      callback()
    }
  },
  isAssetSerialNumberUnique: (apiService, path, uuid) => {
    return isFieldUnique(apiService, path, 'fullSerialNumber', uuid, 'Asset', 'Full Serial Number')
  },
  isPrinterAliasUnique: (apiService, path, uuid) => {
    return isFieldUnique(apiService, path, 'alias', uuid, 'Printer', 'Alias')
  },
  isPrinterNetworkAddressUnique: (apiService, path, uuid) => {
    return isFieldUnique(apiService, path, 'networkAddress', uuid, 'Printer', 'Network Address')
  },
  isPrinterMacAddressUnique: (apiService, path, uuid) => {
    return isFieldUnique(apiService, path, 'macAddress', uuid, 'Printer', 'MAC Address')
  },
  isAssetPropertyNumberUnique: (apiService, path, uuid) => {
    return isFieldUnique(apiService, path, 'propertyNumber', uuid, 'Asset', 'Property Number')
  },
  isComedCategoryIdUnique: (apiService, path, uuid) => {
    return isFieldUnique(apiService, path, 'comedCategoryId', uuid, 'Category', 'Category Id')
  },
  isReadzoneNameUnique: (apiService, path, uuid) => {
    return isFieldUnique(apiService, path, 'name', uuid, 'Location', 'Name')
  },
  validateCoordinates: (coordinatesArray) => {
    return coordinatesArray.every((coord) => {
      return decimalValidator(coord.latitude) && decimalValidator(coord.longitude)
    })
  },
  isContractNoUnique: (apiService, path, uuid) => {
    return isFieldUnique(apiService, path, 'contractNo', uuid, 'Contract', 'Contract Number')
  },
  isReaderMacAddressUnique: (apiService, path, uuid) => {
    return isFieldUnique(apiService, path, 'macAddress', uuid, 'Reader', 'Mac Address')
  },
  isReaderNetworkAddressUnique: (apiService, path, uuid) => {
    return isFieldUnique(apiService, path, 'networkAddress', uuid, 'Reader', 'Mac Address')
  },
  macAddress(rule, value) {
    const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/
    if (value === '') {
      return new Error('Please enter a MAC address')
    }
    if (!macRegex.test(value)) {
      return new Error('Invalid MAC address format')
    }
    return true
  },
}
export { validationRules }
